import { privateApi } from '@/config/axios.config';
import {
  LeadEntity,
  OpportunityEntity,
  InteractionEntity,
  LeadCreateDTO,
  LeadUpdateDTO,
  OpportunityCreateDTO,
  OpportunityUpdateDTO,
  InteractionCreateDTO,
  InteractionUpdateDTO,
  LeadFilterDTO,
  OpportunityFilterDTO,
  InteractionFilterDTO,
  LeadListResponse,
  OpportunityListResponse,
  InteractionListResponse,
  LeadStatsDTO,
  OpportunityStatsDTO,
  InteractionStatsDTO,
} from '../types/crm-types';
import { pedidoCambioMasivo } from './leads-masivo';
import { desenvolverSobre } from '@/features/marketing/api/marketing-api';
import { agregarFiltros, FiltrosExtra } from './filtros-leads';

const CRM_BASE_URL = '/crm';

// === LEADS API ===

export const leadsApi = {
  // Crear nuevo lead
  create: async (data: LeadCreateDTO): Promise<LeadEntity> => {
    const response = await privateApi.post(`${CRM_BASE_URL}/leads`, data);
    return response.data;
  },

  // Obtener todos los leads con filtros
  getAll: async (filters?: LeadFilterDTO): Promise<LeadListResponse> => {
    const params = new URLSearchParams();

    if (filters?.search) params.append('search', filters.search);
    if (filters?.status) params.append('status', filters.status);
    // GHL 7.2 — la etapa del lead (lead.stage); el backend la filtra en lead.service findAll
    const etapa = (filters as { stage?: string } | undefined)?.stage;
    if (etapa) params.append('stage', etapa);
    // GHL 7.2 — fuente, asignado, prioridad, etiqueta y rango de alta con los
    // nombres que lee el backend (antes assignedAgentId/priority se ignoraban).
    agregarFiltros(params, filters as unknown as FiltrosExtra);
    if (filters?.tags?.length) {
      filters.tags.forEach(tag => params.append('tags', tag));
    }
    if (filters?.lastContactedFrom)
      params.append(
        'lastContactedFrom',
        filters.lastContactedFrom.toISOString()
      );
    if (filters?.lastContactedTo)
      params.append('lastContactedTo', filters.lastContactedTo.toISOString());
    if (filters?.minBudget)
      params.append('minBudget', filters.minBudget.toString());
    if (filters?.maxBudget)
      params.append('maxBudget', filters.maxBudget.toString());
    if (filters?.interestedPropertyTypes?.length) {
      filters.interestedPropertyTypes.forEach(type =>
        params.append('interestedPropertyTypes', type)
      );
    }
    if (filters?.preferredLocations?.length) {
      filters.preferredLocations.forEach(location =>
        params.append('preferredLocations', location)
      );
    }
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.sortBy) params.append('sortBy', filters.sortBy);
    if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);

    const response = await privateApi.get(
      `${CRM_BASE_URL}/leads?${params.toString()}`
    );
    return response.data;
  },

  // Obtener lead por ID
  getById: async (id: string): Promise<LeadEntity> => {
    const response = await privateApi.get(`${CRM_BASE_URL}/leads/${id}`);
    return response.data;
  },

  // Actualizar lead
  update: async (id: string, data: LeadUpdateDTO): Promise<LeadEntity> => {
    const response = await privateApi.put(`${CRM_BASE_URL}/leads/${id}`, data);
    return response.data;
  },

  // Eliminar lead
  delete: async (id: string): Promise<void> => {
    await privateApi.delete(`${CRM_BASE_URL}/leads/${id}`);
  },

  // Convertir lead a oportunidad
  convertToOpportunity: async (
    id: string,
    opportunityData?: Partial<OpportunityCreateDTO>
  ): Promise<OpportunityEntity> => {
    const response = await privateApi.post(
      `${CRM_BASE_URL}/leads/${id}/convert`,
      opportunityData || {}
    );
    return response.data;
  },

  // Obtener estadísticas de leads
  getStats: async (): Promise<LeadStatsDTO> => {
    const response = await privateApi.get(`${CRM_BASE_URL}/leads/stats`);
    return response.data;
  },

  // Asignar lead a agente
  assignToAgent: async (
    leadId: string,
    agentId: string
  ): Promise<LeadEntity> => {
    const response = await privateApi.patch(
      `${CRM_BASE_URL}/leads/${leadId}/assign`,
      { assignedAgentId: agentId }
    );
    return response.data;
  },

  // Actualizar score del lead
  updateScore: async (leadId: string, score: number): Promise<LeadEntity> => {
    const response = await privateApi.patch(
      `${CRM_BASE_URL}/leads/${leadId}/score`,
      { score }
    );
    return response.data;
  },

  // Programar seguimiento
  scheduleFollowUp: async (
    leadId: string,
    followUpDate: Date,
    notes?: string
  ): Promise<LeadEntity> => {
    const response = await privateApi.patch(
      `${CRM_BASE_URL}/leads/${leadId}/follow-up`,
      {
        nextFollowUpAt: followUpDate.toISOString(),
        notes,
      }
    );
    return response.data;
  },
};

// === OPPORTUNITIES API ===

export const opportunitiesApi = {
  // Crear nueva oportunidad
  create: async (data: OpportunityCreateDTO): Promise<OpportunityEntity> => {
    const response = await privateApi.post(
      `${CRM_BASE_URL}/opportunities`,
      data
    );
    return response.data;
  },

  // Obtener todas las oportunidades con filtros
  getAll: async (
    filters?: OpportunityFilterDTO
  ): Promise<OpportunityListResponse> => {
    const params = new URLSearchParams();

    if (filters?.search) params.append('search', filters.search);
    if (filters?.stage) params.append('stage', filters.stage);
    if (filters?.leadId) params.append('leadId', filters.leadId);
    if (filters?.propertyId) params.append('propertyId', filters.propertyId);
    if (filters?.assignedAgentId)
      params.append('assignedAgentId', filters.assignedAgentId);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.minValue)
      params.append('minValue', filters.minValue.toString());
    if (filters?.maxValue)
      params.append('maxValue', filters.maxValue.toString());
    if (filters?.expectedCloseDateFrom)
      params.append(
        'expectedCloseDateFrom',
        filters.expectedCloseDateFrom.toISOString()
      );
    if (filters?.expectedCloseDateTo)
      params.append(
        'expectedCloseDateTo',
        filters.expectedCloseDateTo.toISOString()
      );
    if (filters?.tags?.length) {
      filters.tags.forEach(tag => params.append('tags', tag));
    }
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.sortBy) params.append('sortBy', filters.sortBy);
    if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);

    const response = await privateApi.get(
      `${CRM_BASE_URL}/opportunities?${params.toString()}`
    );
    return response.data;
  },

  // Obtener oportunidad por ID
  getById: async (id: string): Promise<OpportunityEntity> => {
    const response = await privateApi.get(
      `${CRM_BASE_URL}/opportunities/${id}`
    );
    return response.data;
  },

  // Actualizar oportunidad
  update: async (
    id: string,
    data: OpportunityUpdateDTO
  ): Promise<OpportunityEntity> => {
    const response = await privateApi.put(
      `${CRM_BASE_URL}/opportunities/${id}`,
      data
    );
    return response.data;
  },

  // Eliminar oportunidad
  delete: async (id: string): Promise<void> => {
    await privateApi.delete(`${CRM_BASE_URL}/opportunities/${id}`);
  },

  // Obtener estadísticas de oportunidades
  getStats: async (): Promise<OpportunityStatsDTO> => {
    const response = await privateApi.get(
      `${CRM_BASE_URL}/opportunities/stats`
    );
    return response.data;
  },

  // Obtener oportunidades vencidas
  getOverdue: async (): Promise<OpportunityEntity[]> => {
    const response = await privateApi.get(
      `${CRM_BASE_URL}/opportunities/overdue`
    );
    return response.data;
  },

  // Obtener oportunidades por lead
  getByLead: async (leadId: string): Promise<OpportunityEntity[]> => {
    const response = await privateApi.get(
      `${CRM_BASE_URL}/opportunities/lead/${leadId}`
    );
    return response.data;
  },

  // Mover oportunidad a siguiente etapa
  moveToNextStage: async (id: string): Promise<OpportunityEntity> => {
    const response = await privateApi.patch(
      `${CRM_BASE_URL}/opportunities/${id}/next-stage`
    );
    return response.data;
  },

  // Marcar como ganada
  markAsWon: async (
    id: string,
    actualCloseDate?: Date,
    winningFactors?: string[]
  ): Promise<OpportunityEntity> => {
    const response = await privateApi.patch(
      `${CRM_BASE_URL}/opportunities/${id}/won`,
      {
        actualCloseDate: actualCloseDate?.toISOString(),
        winningFactors,
      }
    );
    return response.data;
  },

  // Marcar como perdida
  markAsLost: async (
    id: string,
    reasonLost: string
  ): Promise<OpportunityEntity> => {
    const response = await privateApi.patch(
      `${CRM_BASE_URL}/opportunities/${id}/lost`,
      {
        reasonLost,
      }
    );
    return response.data;
  },
};

// === INTERACTIONS API ===

export const interactionsApi = {
  // Crear nueva interacción
  create: async (data: InteractionCreateDTO): Promise<InteractionEntity> => {
    const response = await privateApi.post(
      `${CRM_BASE_URL}/interactions`,
      data
    );
    return response.data;
  },

  // Obtener todas las interacciones con filtros
  getAll: async (
    filters?: InteractionFilterDTO
  ): Promise<InteractionEntity[]> => {
    const params = new URLSearchParams();

    if (filters?.search) params.append('search', filters.search);
    if (filters?.type) params.append('type', filters.type);
    if (filters?.leadId) params.append('leadId', filters.leadId);
    if (filters?.opportunityId)
      params.append('opportunityId', filters.opportunityId);
    if (filters?.propertyId) params.append('propertyId', filters.propertyId);
    if (filters?.agentId) params.append('agentId', filters.agentId);
    if (filters?.completed !== undefined)
      params.append('completed', filters.completed.toString());
    if (filters?.scheduledFrom)
      params.append('scheduledFrom', filters.scheduledFrom.toISOString());
    if (filters?.scheduledTo)
      params.append('scheduledTo', filters.scheduledTo.toISOString());
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.tags?.length) {
      filters.tags.forEach(tag => params.append('tags', tag));
    }
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.sortBy) params.append('sortBy', filters.sortBy);
    if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);

    const response = await privateApi.get(
      `${CRM_BASE_URL}/interactions?${params.toString()}`
    );
    // GH#1662 — el backend devuelve InteractionEntity[] (interaction.controller.ts:73)
    // y el interceptor global lo envuelve en {success, data}. Sin desenvolver,
    // ViewInteractionHistoryModal leia `response.interactions` (no existe) y el
    // historial salia SIEMPRE vacio; su gemelo de broker ya lo compensaba a mano.
    return desenvolverSobre<InteractionEntity[]>(response.data);
  },

  // Obtener interacción por ID
  getById: async (id: string): Promise<InteractionEntity> => {
    const response = await privateApi.get(`${CRM_BASE_URL}/interactions/${id}`);
    return response.data;
  },

  // Actualizar interacción
  update: async (
    id: string,
    data: InteractionUpdateDTO
  ): Promise<InteractionEntity> => {
    const response = await privateApi.put(
      `${CRM_BASE_URL}/interactions/${id}`,
      data
    );
    return response.data;
  },

  // Eliminar interacción
  delete: async (id: string): Promise<void> => {
    await privateApi.delete(`${CRM_BASE_URL}/interactions/${id}`);
  },

  // Obtener estadísticas de interacciones
  getStats: async (): Promise<InteractionStatsDTO> => {
    const response = await privateApi.get(`${CRM_BASE_URL}/interactions/stats`);
    return response.data;
  },

  // Obtener interacciones por lead
  getByLead: async (leadId: string): Promise<InteractionEntity[]> => {
    const response = await privateApi.get(
      `${CRM_BASE_URL}/interactions/lead/${leadId}`
    );
    return response.data;
  },

  // Marcar interacción como completada
  markAsCompleted: async (
    id: string,
    outcome?: string
  ): Promise<InteractionEntity> => {
    const response = await privateApi.patch(
      `${CRM_BASE_URL}/interactions/${id}/complete`,
      {
        completed: true,
        outcome,
      }
    );
    return response.data;
  },

  // Reprogramar interacción
  reschedule: async (id: string, newDate: Date): Promise<InteractionEntity> => {
    const response = await privateApi.patch(
      `${CRM_BASE_URL}/interactions/${id}/reschedule`,
      {
        scheduledAt: newDate.toISOString(),
      }
    );
    return response.data;
  },

  // Agregar recordatorio
  addReminder: async (
    id: string,
    reminderDate: Date
  ): Promise<InteractionEntity> => {
    const response = await privateApi.patch(
      `${CRM_BASE_URL}/interactions/${id}/reminder`,
      {
        reminderSet: true,
        // La fecha del recordatorio se manejará en el backend
      }
    );
    return response.data;
  },

  // Subir archivo adjunto
  uploadAttachment: async (
    interactionId: string,
    file: File
  ): Promise<InteractionEntity> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await privateApi.post(
      `${CRM_BASE_URL}/interactions/${interactionId}/attachments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  // Eliminar archivo adjunto
  deleteAttachment: async (
    interactionId: string,
    attachmentId: string
  ): Promise<InteractionEntity> => {
    const response = await privateApi.delete(
      `${CRM_BASE_URL}/interactions/${interactionId}/attachments/${attachmentId}`
    );
    return response.data;
  },
};

// === BULK OPERATIONS ===

export const crmBulkApi = {
  // GHL 7.4 — la ruta real es /crm/leads/import/csv (decía /leads/import, que no existe)
  importLeads: async (
    file: File | Blob,
  ): Promise<{ imported: number; errors: { row: number; message: string }[] }> => {
    const formData = new FormData();
    formData.append('file', file, (file as File).name || 'leads.csv');
    const response = await privateApi.post(`${CRM_BASE_URL}/leads/import/csv`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const p = response.data?.data ?? response.data;
    return { imported: Number(p?.imported) || 0, errors: Array.isArray(p?.errors) ? p.errors : [] };
  },

  // Exportar leads
  exportLeads: async (filters?: LeadFilterDTO): Promise<Blob> => {
    const params = new URLSearchParams();
    // ... agregar filtros si es necesario

    const response = await privateApi.get(
      `${CRM_BASE_URL}/leads/export?${params.toString()}`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  },

  // GHL 7.6 — las rutas reales son POST /crm/leads/bulk/* (ver leads-masivo.ts)
  assignMultipleLeads: async (leadIds: string[], agentId: string) => {
    const response = await privateApi.post(`${CRM_BASE_URL}/leads/bulk/assign`, {
      leadIds,
      assignedToId: agentId,
    });
    return response.data;
  },

  updateMultipleLeads: async (leadIds: string[], updates: { stage?: string; priority?: string }) => {
    const { ruta, cuerpo } = pedidoCambioMasivo(leadIds, updates);
    const response = await privateApi.post(ruta, cuerpo);
    return response.data;
  },

  // GHL 7.6 — etiquetar en lote: el backend valida leads y etiquetas del tenant (lead-tag.service bulkAssignToLeads)
  tagMultipleLeads: async (leadIds: string[], tagIds: string[]) => {
    const response = await privateApi.post(`${CRM_BASE_URL}/tags/bulk-assign`, { leadIds, tagIds });
    return response.data?.data ?? response.data;
  },

  deleteMultipleLeads: async (leadIds: string[]) => {
    const response = await privateApi.post(`${CRM_BASE_URL}/leads/bulk/soft-delete`, { leadIds });
    return response.data;
  },
};

// === REPORTES Y ANALYTICS ===

export const crmReportsApi = {
  // Reporte de pipeline de ventas
  getSalesPipeline: async (
    agentId?: string,
    dateFrom?: Date,
    dateTo?: Date
  ) => {
    const params = new URLSearchParams();
    if (agentId) params.append('agentId', agentId);
    if (dateFrom) params.append('dateFrom', dateFrom.toISOString());
    if (dateTo) params.append('dateTo', dateTo.toISOString());

    const response = await privateApi.get(
      `${CRM_BASE_URL}/reports/sales-pipeline?${params.toString()}`
    );
    return response.data;
  },

  // Reporte de conversión de leads
  getLeadConversion: async (period: 'week' | 'month' | 'quarter' | 'year') => {
    const response = await privateApi.get(
      `${CRM_BASE_URL}/reports/lead-conversion?period=${period}`
    );
    return response.data;
  },

  // Reporte de rendimiento de agentes
  getAgentPerformance: async (agentId?: string, period?: string) => {
    const params = new URLSearchParams();
    if (agentId) params.append('agentId', agentId);
    if (period) params.append('period', period);

    const response = await privateApi.get(
      `${CRM_BASE_URL}/reports/agent-performance?${params.toString()}`
    );
    return response.data;
  },

  // Reporte de fuentes de leads
  getLeadSources: async (dateFrom?: Date, dateTo?: Date) => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('dateFrom', dateFrom.toISOString());
    if (dateTo) params.append('dateTo', dateTo.toISOString());

    const response = await privateApi.get(
      `${CRM_BASE_URL}/reports/lead-sources?${params.toString()}`
    );
    return response.data;
  },
};

// Exportar todas las APIs del CRM
export const crmApi = {
  leads: leadsApi,
  opportunities: opportunitiesApi,
  interactions: interactionsApi,
  bulk: crmBulkApi,
  reports: crmReportsApi,
};

export default crmApi;
